// The Fallbacks card, the Models Fallbacks sub-tab (FALLBACK-1, SCR-590): one row per model the org has a
// fallback chain for — "When <Opus 5.5> is unavailable, use <Sonnet 5.5>, then <…>" — with selects
// that offer only models every agent using it may use (display names only), add, remove, and move
// earlier/later as buttons (keyboard and screen reader reachable, no drag). The card saves on its
// own; a 422 lands on the row it names. The chains are the org's: at an
// installation's scope they are shown read-only, "set for the whole org".
import type { ApiError } from "../../api/errors";
import { errorId, Field } from "../../primitives/field";
import type { HelpId } from "../../primitives/help";
import { Card } from "../../primitives/page";
import { Empty, ErrorBanner } from "../../primitives/states";
import type { JsonObject } from "../../settings/inherit";
import { addRow, addTarget, fallbackRows, fromChoices, MAX_TARGETS, moveTarget, removeRow, removeTarget, setFrom, setTarget, targetChoices, type RowError } from "./fallbacks-model";
import type { CatalogModel } from "./provider-model";

const HELP: HelpId = "settings.models.fallbacks";

/** What the card says about how a fallback behaves; the help topic says the same. */
export const FALLBACK_NOTES = [
  "A model counts as unavailable only when AWS Bedrock says it has no capacity for it (it answers “unable to process” or throttles the request) before anything has been sent back. A refused or invalid request, a guardrail block, or an answer that has already started is never retried.",
  "Each fallback is checked like a new request: the agent must be allowed the model, and the guardrail and usage limits apply.",
  "A fallback model starts without the conversation’s prompt cache, so its first answer costs more and may take longer.",
  "Usage and billing show the model that actually answered, and note the model it stood in for.",
] as const;

export interface FallbacksSectionProps {
  /** The edited document (the org default); the card's draft starts from it. */
  draft: JsonObject;
  catalog: readonly CatalogModel[];
  /** False at an installation's scope: the chains are set for the whole org. */
  editable: boolean;
  /** Why the controls are disabled (read-only proxy); null when they are not. */
  disabledNote: string | null;
  /** The last save's refusals, by row. */
  errors: Readonly<Record<number, RowError>>;
  /** A failure the rows do not place: the card's banner. */
  failure: ApiError | null;
  changed: boolean;
  saving: boolean;
  onChange(next: JsonObject): void;
  onSave(): void;
  onOpenOrg?: (() => void) | undefined;
}

export function FallbacksSection({ draft, catalog, editable, disabledNote, errors, failure, changed, saving, onChange, onSave, onOpenOrg }: FallbacksSectionProps) {
  const rows = fallbackRows(draft, catalog);
  const disabled = !editable || disabledNote !== null || saving;
  const canAdd = !disabled && addRow(draft, catalog) !== draft;
  return (
    <Card title="Fallbacks" help={HELP}>
      <p class="card-note">When AWS has no capacity for a model, the proxy tries the models you list for it, in order.</p>
      <ul class="field-hint fallback-notes">
        {FALLBACK_NOTES.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
      {!editable && (
        <p class="field-hint">
          Set for the whole org.{" "}
          {onOpenOrg && (
            <button type="button" class="button-link" onClick={onOpenOrg}>
              Edit the org default
            </button>
          )}
        </p>
      )}
      {editable && disabledNote && <p class="field-hint">{disabledNote}</p>}
      {rows.length === 0 ? (
        <Empty message="No fallbacks: when a model is unavailable, the request fails as AWS answered it." />
      ) : (
        <ol class="fallback-rows">
          {rows.map((row, index) => {
            const error = errors[index];
            const fromId = `fallback-${index}-from`;
            const chain = row.targets.map((t) => t.model);
            return (
              <li key={`${row.from}-${index}`} class="fallback-row" aria-label={row.sentence}>
                <div class="fallback-row-line">
                  <Field id={fromId} label="When this model is unavailable" help={HELP} error={error?.target === null ? error.message : null}>
                    <select id={fromId} value={row.from} disabled={disabled} aria-invalid={error?.target === null ? true : undefined} aria-describedby={error?.target === null ? errorId(fromId) : undefined} onChange={(event) => onChange(setFrom(draft, index, event.currentTarget.value, catalog))}>
                      {fromChoices(draft, catalog, row.from).map((m) => (
                        <option key={m.model} value={m.model}>
                          {m.displayName}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <button type="button" class="button button-secondary button-small" disabled={disabled} aria-label={`Remove the fallbacks for ${row.fromName}`} onClick={() => onChange(removeRow(draft, index))}>
                    Remove
                  </button>
                </div>
                <ol class="fallback-targets">
                  {row.targets.map((target, slot) => {
                    const id = `fallback-${index}-to-${slot}`;
                    const slotError = error?.target === slot ? error.message : null;
                    return (
                      <li key={`${target.model}-${slot}`} class="fallback-target">
                        <Field id={id} label={slot === 0 ? "Use" : "Then"} help={HELP} error={slotError}>
                          <select id={id} value={target.model} disabled={disabled} aria-invalid={slotError ? true : undefined} aria-describedby={slotError ? errorId(id) : undefined} onChange={(event) => onChange(setTarget(draft, index, slot, event.currentTarget.value))}>
                            {targetChoices(draft, catalog, row.from, chain, slot).map((m) => (
                              <option key={m.model} value={m.model}>
                                {m.displayName}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <button type="button" class="button button-secondary button-small" disabled={disabled || slot === 0} aria-label={`Move ${target.name} earlier`} title={`Move ${target.name} earlier`} onClick={() => onChange(moveTarget(draft, index, slot, -1))}>
                          <span aria-hidden="true">↑</span> Earlier
                        </button>
                        <button type="button" class="button button-secondary button-small" disabled={disabled || slot === row.targets.length - 1} aria-label={`Move ${target.name} later`} title={`Move ${target.name} later`} onClick={() => onChange(moveTarget(draft, index, slot, 1))}>
                          <span aria-hidden="true">↓</span> Later
                        </button>
                        <button type="button" class="button button-secondary button-small" disabled={disabled} aria-label={`Remove ${target.name} from the fallbacks for ${row.fromName}`} onClick={() => onChange(removeTarget(draft, index, slot))}>
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ol>
                {row.targets.length < MAX_TARGETS && (
                  <button type="button" class="button-link" disabled={disabled || addTarget(draft, index, catalog) === draft} onClick={() => onChange(addTarget(draft, index, catalog))}>
                    Add another fallback for {row.fromName}
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {failure && <ErrorBanner error={failure} details={true} />}
      {editable && (
        <div class="row">
          <button type="button" class="button button-secondary" disabled={!canAdd} onClick={() => onChange(addRow(draft, catalog))}>
            Add a fallback
          </button>
          <button type="button" class="button button-primary" disabled={disabled || !changed} onClick={onSave}>
            {saving ? "Saving" : "Save fallbacks"}
          </button>
        </div>
      )}
    </Card>
  );
}
